import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType } from "@zxing/library";
import { Camera, CameraOff, Keyboard, QrCode, ScanLine, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { resolveCode } from "../../lib/resolveCode";

/**
 * Live-Video QR/Barcode-Scanner auf Basis von @zxing/browser.
 * Fällt auf Keyboard-Input zurück wenn keine Kamera verfügbar oder
 * Permission verweigert.
 *
 * Jeder Treffer (Kamera oder manuelle Eingabe) läuft durch den zentralen
 * resolveCode()-Resolver -- damit funktionieren rohe EANs, Seriennummern
 * und Fach-Codes überall gleich, nicht nur im Scan-Hub.
 *
 * Props:
 *   placeholder -- Hint-Text
 *   onResolved(raw, resolved?) -- Callback. `resolved` ist das Ergebnis von
 *     resolveCode() plus `id` (= entity.id, immer die echte Entity-ID) --
 *     oder null, wenn der Code nicht zugeordnet werden konnte.
 *   autoStart -- wenn true: Kamera startet direkt (Default true)
 *   continuous -- wenn true: nach Erkennung weitermachen, sonst stoppen
 *   restartKey -- geänderter Wert startet die Kamera erneut, z. B. nach einer
 *     abgeschlossenen Buchung
 *   openManualKey -- geänderter Wert öffnet die manuelle Eingabe
 */
export function ScanBox({
    placeholder = "Fach, Artikel oder Ausleihgegenstand scannen",
    onResolved,
    autoStart = true,
    continuous = false,
    restartKey = 0,
    openManualKey = 0,
    testId = "scanbox",
}) {
    const videoRef = useRef(null);
    const controlsRef = useRef(null); // IScannerControls
    const disposedRef = useRef(false);
    const resolvingRef = useRef(false);
    const lastHitRef = useRef({ raw: null, at: 0 });
    // The camera callback outlives a render; always use current flow state (#190/#207).
    const onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;
    const continuousRef = useRef(continuous);
    continuousRef.current = continuous;
    const [active, setActive] = useState(false);
    const [cameraError, setCameraError] = useState(null);
    const [manualOpen, setManualOpen] = useState(false);
    const [value, setValue] = useState("");
    const [lastHit, setLastHit] = useState(null);

    // Continuous-Decoder feuert denselben sichtbaren Code mehrfach pro
    // Sekunde -- gleicher Rohwert innerhalb des Fensters wird verworfen.
    const DEDUPE_MS = 2500;

    const start = async () => {
        if (controlsRef.current) return;
        setCameraError(null);
        if (!navigator.mediaDevices?.getUserMedia) {
            setCameraError("no-api");
            return;
        }
        try {
            // 1. Prefer environment-facing camera via explicit stream
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: "environment" } },
                audio: false,
            });
            // Unmount während getUserMedia(): Tracks sofort wieder stoppen,
            // sonst bleibt die Kamera an, obwohl niemand mehr zuhört.
            if (disposedRef.current || !videoRef.current) {
                stream.getTracks().forEach((t) => t.stop());
                return;
            }
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => {});
            // 2. Hand that stream to zxing decoder
            // Drawer labels put a large number beside Code 128. Searching more
            // rows finds bars above/below the text instead of stopping on it.
            const reader = new BrowserMultiFormatReader(new Map([[DecodeHintType.TRY_HARDER, true]]));
            const controls = await reader.decodeFromVideoElement(
                videoRef.current,
                (result) => {
                    if (result) {
                        handleHit(result.getText());
                    }
                }
            );
            if (disposedRef.current) {
                controls.stop();
                stream.getTracks().forEach((t) => t.stop());
                return;
            }
            controlsRef.current = { controls, stream };
            setActive(true);
        } catch (e) {
            setCameraError(e.name || "no-permission");
            setActive(false);
        }
    };

    const stop = () => {
        try {
            controlsRef.current?.controls?.stop();
            controlsRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
        } catch (_e) { /* ignore */ }
        controlsRef.current = null;
        setActive(false);
    };

    const handleHit = async (raw) => {
        if (resolvingRef.current) return;
        const now = Date.now();
        if (lastHitRef.current.raw === raw && now - lastHitRef.current.at < DEDUPE_MS) return;
        lastHitRef.current = { raw, at: now };
        setLastHit(raw);
        if (!continuousRef.current) stop();
        resolvingRef.current = true;
        try {
            const resolved = await resolveCode(raw);
            if (disposedRef.current) return;
            onResolvedRef.current?.(raw, resolved.ok ? { ...resolved, id: resolved.entity.id } : null);
        } finally {
            resolvingRef.current = false;
        }
    };

    const manualSubmit = () => {
        const v = value.trim();
        if (!v) return;
        setValue("");
        setManualOpen(false);
        // bewusste Eingabe: nie vom Kamera-Dedupe verschlucken lassen
        lastHitRef.current = { raw: null, at: 0 };
        handleHit(v);
    };

    useEffect(() => {
        disposedRef.current = false;
        if (autoStart) start();
        return () => {
            disposedRef.current = true;
            stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoStart]);

    useEffect(() => {
        if (restartKey > 0) start();
        // `start` liest absichtlich den aktuellen Scanner-Ref. Der Key ist
        // der einzige Trigger; ein Funktionswechsel darf nicht neu starten.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restartKey]);

    useEffect(() => {
        if (openManualKey > 0) setManualOpen(true);
    }, [openManualKey]);

    return (
        <div className="relative" data-testid={testId}>
            <div className="relative flex h-56 w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-black">
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`h-full w-full object-cover ${active ? "opacity-100" : "opacity-0"}`}
                    data-testid={`${testId}-video`}
                />
                <div className="pointer-events-none absolute inset-x-6 inset-y-8 rounded-lg border-2 border-primary/70" />
                <div className="pointer-events-none absolute inset-x-6 top-10 flex justify-between">
                    <div className="h-8 w-8 rounded-tl-lg border-l-2 border-t-2 border-primary" />
                    <div className="h-8 w-8 rounded-tr-lg border-r-2 border-t-2 border-primary" />
                </div>
                <div className="pointer-events-none absolute inset-x-6 bottom-10 flex justify-between">
                    <div className="h-8 w-8 rounded-bl-lg border-b-2 border-l-2 border-primary" />
                    <div className="h-8 w-8 rounded-br-lg border-b-2 border-r-2 border-primary" />
                </div>

                {!active ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-muted-foreground">
                        {cameraError ? (
                            <>
                                <CameraOff className="h-10 w-10" />
                                <span className="text-xs uppercase tracking-[0.2em]">
                                    Kamera nicht verfügbar
                                </span>
                                <span className="max-w-[220px] text-center text-xs">
                                    Bitte Kamera-Berechtigung erteilen oder manuelle Eingabe nutzen.
                                </span>
                                <div className="flex gap-2">
                                    <Button size="sm" variant="outline" onClick={start} data-testid={`${testId}-retry`}>
                                        <Camera className="mr-1 h-4 w-4" /> Erneut versuchen
                                    </Button>
                                    <Button size="sm" onClick={() => setManualOpen(true)} data-testid={`${testId}-fallback-manual`}>
                                        <Keyboard className="mr-1 h-4 w-4" /> Manuell
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <>
                                <ScanLine className="h-10 w-10 text-primary" />
                                <span className="text-xs uppercase tracking-[0.2em]">
                                    Scanner bereit
                                </span>
                                <Button size="sm" onClick={start} data-testid={`${testId}-start`}>
                                    <Camera className="mr-1 h-4 w-4" /> Starten
                                </Button>
                            </>
                        )}
                    </div>
                ) : null}

                <div className="absolute right-2 top-2 flex gap-1">
                    {active ? (
                        <button
                            type="button"
                            className="pw-tap rounded-full bg-card/80 p-2"
                            onClick={stop}
                            aria-label="Scanner pausieren"
                            data-testid={`${testId}-stop`}
                        >
                            <X className="h-4 w-4 text-foreground" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        className="pw-tap rounded-full bg-card/80 p-2"
                        onClick={() => setManualOpen(true)}
                        aria-label="Code manuell eingeben"
                        data-testid={`${testId}-manual-open`}
                    >
                        <Keyboard className="h-4 w-4 text-foreground" />
                    </button>
                </div>

                <div className="absolute bottom-2 left-3 text-xs uppercase tracking-widest text-muted-foreground">
                    {placeholder}
                </div>
            </div>

            {lastHit && continuous ? (
                <div className="mt-2 rounded-md border border-border/60 bg-card/40 px-3 py-2 text-xs text-muted-foreground">
                    Zuletzt erkannt: <code className="text-foreground">{lastHit}</code>
                </div>
            ) : null}

            {manualOpen ? (
                <div
                    role="dialog"
                    className="absolute inset-0 z-40 flex flex-col gap-3 rounded-xl border border-border bg-card/95 p-4 shadow-2xl"
                >
                    <div className="flex items-center gap-2">
                        <QrCode className="h-5 w-5 text-primary" />
                        <span className="text-sm font-medium">Code manuell eingeben</span>
                        <button
                            type="button"
                            className="pw-tap ml-auto"
                            onClick={() => setManualOpen(false)}
                            aria-label="schließen"
                            data-testid={`${testId}-manual-close`}
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>
                    <Input
                        autoFocus
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        placeholder="platzwart://... oder EAN / Fach-Code"
                        aria-label="Code manuell eingeben"
                        className="h-12"
                        onKeyDown={(e) => e.key === "Enter" && manualSubmit()}
                        data-testid={`${testId}-manual-input`}
                    />
                    <Button
                        onClick={manualSubmit}
                        className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid={`${testId}-manual-submit`}
                    >
                        Übernehmen
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
